import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { Anchor } from '@americanexpress/dls-react';
import { prospectFetch } from '@americanexpress/axp-acquisition-components-common';
import { createAnalyticsData, noop } from '../../utils';
import InEligibleError from '../InEligibleError/InEligibleError';

const ErrorPageContext = React.createContext();

export const useErrorContext = () => React.useContext(ErrorPageContext);

class ErrorPageProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showError: false,
      logData: {},
      analyticTag: 'SystemError',
      errorMessage: 'default',
      analyticsData: {},
    };
  }

  static getDerivedStateFromError(error) {
    return { showError: true, logData: { message: `System Error - ${error.message}` } };
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { showError, logData } = this.state;
    if (logData.message && !prevState.logData.message) {
      this.logError();
    }
    if (showError && !prevState.showError) {
      this.handleAnalyticsImpression();
    }
  }

  setAnalyticsData = (analyticsData) => {
    this.setState({ analyticsData });
  }

  handleAnalyticsImpression = async () => {
    const { track } = this.props;
    const { analyticsData, analyticTag } = this.state;
    const { product = {} } = analyticsData;
    const data = {
      element: 'Error',
      detail: '',
      product,
      meta: {
        conversionStep: undefined,
        siteerror: analyticTag === 'timeout' ? 'Timeout' : 'SystemError',
      },
    };
    try {
      await track(createAnalyticsData(data));
    } catch (e) {
      // do nothing
    }
  }

  handleError = ({
    logData,
    analyticTag = 'SystemError',
  }) => {
    this.setState({
      showError: true,
      logData,
      analyticTag,
      errorMessage: logData.message,
    });
  };

  logError = () => {
    const { host, intl: { messages: { locale } } } = this.props;
    const { logData } = this.state;

    prospectFetch(`${host}/application/v1/log-event`, {
      method: 'POST',
      locale,
      body: JSON.stringify({
        messageType: 'ERROR',
        ...logData,
      }),
    });
  }

  render() {
    const {
      children,
      intl: { messages: { links: { customerService, home } } },
    } = this.props;
    const { showError, analyticTag, errorMessage } = this.state;

    if (showError) {
      if (analyticTag === 'IneligibleError') {
        return (
          <div
            id="inEligibleErrorPage"
            className="dls-white-bg border pad-2-tb pad-3-lr"
          >
            <InEligibleError />
          </div>
        );
      }
      if (errorMessage === 'Timeout') {
        return (
          <div id="timeoutErrorPage">
            <h3 className="heading-4 flex flex-column flex-justify-center text-align-center pad-4-t pad-2-b">
              <span><FormattedMessage id="errorPage.message.TimeoutMain" /></span>
              <span><FormattedMessage id="errorPage.message.TimeoutSub" /></span>
            </h3>
            <div className="flex flex-justify-center pad-2-t pad-4-b">
              <a
                aria-label="ReturnHome"
                className="btn"
                type="button"
                id="returnToHomePageErrorButton"
                href={home}
              >
                <FormattedMessage id="returnHome.button" />
              </a>
            </div>
          </div>
        );
      }
      return (
        <div
          id="errorPage"
          className="dls-white-bg flex flex-column flex-justify-center flex-align-items-center pad-2-tb pad-3-lr"
        >
          <div className=" col-md-5 col-sm-6 pad-3-t">
            <div className="text-wrap text-align-center pad-2-t heading-4">
              <FormattedMessage id="page.error.message" />
            </div>
            <div className="text-wrap text-align-center margin-1-lr pad-1-t">
              <FormattedMessage
                id="page.error.text"
                values={{
                  a: (
                    <Anchor href={customerService} rel="noreferrer noopener" target="_blank">
                      <FormattedMessage id="customerService.link" />
                    </Anchor>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <ErrorPageContext.Provider
        value={{
          handleError: this.handleError,
          setAnalyticsData: this.setAnalyticsData,
        }}
      >
        {children}
      </ErrorPageContext.Provider>
    );
  }
}

ErrorPageProvider.propTypes = {
  children: PropTypes.node,
  host: PropTypes.string.isRequired,
  intl: PropTypes.shape(intlShape),
  track: PropTypes.func,
};

ErrorPageProvider.defaultProps = {
  children: null,
  track: noop,
};

export default injectIntl(ErrorPageProvider);
